import React, { useState } from "react";
import axios from "axios";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { produce } from "immer";
import type { Character } from "../types/characterTypes";
import {
  handleChange,
  handleChangeArray,
  handleChangeNestedArray,
  addListItem,
} from "../lib/CharacterFormLogic";


interface AddCharacterFormProps {
  onClose?: () => void;
}

const initialCharacter: Character = {
  identity: {
    name: "",
    age: "",
    gender: "",
    race: "",
    role: "",
    affiliations: "",
  },
  physicalDescription: {
    height: "",
    eyeColor: "",
    hairColor: "",
    skinColor: "",
    features: "",
    style: "",
  },
  personality: {
    coreTraits: "",
    strengths: "",
    weaknesses: "",
    fears: "",
    motivations: "",
    mannerisms: "",
  },
  backstory: {
    keyEvents: [{ when: "", eventDescription: "", outcome: "" }],
    birthplace: "",
    relationships: [
      {
        allies: [{ name: "", relationship: "", description: "" }],
        enemies: [{ name: "", relationship: "", description: "" }],
        familyMembers: [{ name: "", relationship: "", description: "" }],
      },
    ],
  },
  goals: {
    wants: [{ description: "", timeline: "" }],
    obstacles: [{ description: "" }],
    internalStruggles: [{ description: "" }],
    externalChallenges: [{ description: "" }],
  },
};

const AddCharacterForm: React.FC<AddCharacterFormProps> = ({ onClose }) => {
  const { user } = useAuth();
  const userId = user?.uid ?? "";

  const [character, setCharacter] = useState<Character>(initialCharacter);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await axios.post(api.saveCharacter(userId), character);

      // Reset form on success
      setCharacter(initialCharacter);
      if (onClose) onClose();
    } catch (error) {
      console.error("Error saving character:", error);
      // Optionally display error to user here
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-8 max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold">Character Creator</h2>

      {/* Identity Section */}
      <section>
        <h3 className="text-xl font-semibold">Identity</h3>
        {Object.entries(character.identity).map(([key, value]) => (
          <input
            key={key}
            type="text"
            placeholder={key}
            className="block w-full border p-2 my-1"
            value={value}
            onChange={(e) => handleChange("identity", key, e.target.value, setCharacter)}
          />
        ))}
      </section>

      {/* Physical Description Section */}
      <section>
        <h3 className="text-xl font-semibold">Physical Description</h3>
        {Object.entries(character.physicalDescription).map(([key, value]) => (
          <input
            key={key}
            type="text"
            placeholder={key}
            className="block w-full border p-2 my-1"
            value={value}
            onChange={(e) =>
              handleChange("physicalDescription", key, e.target.value, setCharacter)
            }
          />
        ))}
      </section>

      {/* Personality Section */}
      <section>
        <h3 className="text-xl font-semibold">Personality</h3>
        {Object.entries(character.personality).map(([key, value]) => (
          <input
            key={key}
            type="text"
            placeholder={key}
            className="block w-full border p-2 my-1"
            value={value}
            onChange={(e) => handleChange("personality", key, e.target.value, setCharacter)}
          />
        ))}
      </section>

      {/* Backstory Section */}
      <section>
        <h3 className="text-xl font-semibold">Backstory</h3>
        <input
          type="text"
          placeholder="Birthplace"
          className="block w-full border p-2 my-1"
          value={character.backstory.birthplace}
          onChange={(e) => handleChange("backstory", "birthplace", e.target.value, setCharacter)}
        />

        <h4 className="font-medium mt-4">Key Events</h4>
        {character.backstory.keyEvents.map((event, i) => (
          <div key={i} className="border p-2 mb-2">
            <input
              type="text"
              placeholder="When"
              className="block w-full mb-1"
              value={event.when}
              onChange={(e) =>
                handleChangeArray("backstory", "keyEvents", i, "when", e.target.value, setCharacter)
              }
            />
            <input
              type="text"
              placeholder="Event Description"
              className="block w-full mb-1"
              value={event.eventDescription}
              onChange={(e) =>
                handleChangeArray(
                  "backstory",
                  "keyEvents",
                  i,
                  "eventDescription",
                  e.target.value, 
                  setCharacter
                )
              }
            />
            <input
              type="text"
              placeholder="Outcome"
              className="block w-full"
              value={event.outcome}
              onChange={(e) =>
                handleChangeArray("backstory", "keyEvents", i, "outcome", e.target.value, setCharacter)
              }
            />
          </div>
        ))}
        <button
          type="button"
          className="text-blue-600 underline"
          onClick={() =>
            addListItem("backstory", "keyEvents", undefined, {
              when: "",
              eventDescription: "",
              outcome: "",
            },
            setCharacter
          )}
        >
          Add Key Event
        </button>

        <h4 className="font-medium mt-4">Relationships</h4>
        {character.backstory.relationships.map((relationship, i) => (
          <div key={i} className="border p-2 mb-2">
            <h4 className="font-medium mt-4">Allies</h4>
            {relationship.allies.map((allies, a) => (
              <div key={a} className="border p-2 mb-2">
                <input
                  type="text"
                  placeholder="Name"
                  className="block w-full mb-1"
                  value={allies.name}
                  onChange={(e) =>
                    handleChangeNestedArray(
                      "backstory",
                      "relationships",
                      "allies",
                      i,
                      a,
                      "name",
                      e.target.value, 
                      setCharacter
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Relationship"
                  className="block w-full mb-1"
                  value={allies.relationship}
                  onChange={(e) =>
                    handleChangeNestedArray(
                      "backstory",
                      "relationships",
                      "allies",
                      i,
                      a,
                      "relationship",
                      e.target.value, 
                      setCharacter
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Description"
                  className="block w-full"
                  value={allies.description}
                  onChange={(e) =>
                    handleChangeNestedArray(
                      "backstory",
                      "relationships",
                      "allies",
                      i,
                      a,
                      "description",
                      e.target.value, 
                      setCharacter
                    )
                  }
                />
              </div>
            ))}
            <button
              type="button"
              className="text-blue-600 underline"
              onClick={() =>
                addListItem(
                  "backstory",
                  "relationships",
                  "allies",
                  { name: "", relationship: "", description: "" },
                  setCharacter,
                  i
                )
              }
            >
              Add Ally
            </button>

            <h4 className="font-medium mt-4">Enemy</h4>
            {relationship.enemies.map((enemies, n) => (
              <div key={n} className="border p-2 mb-2">
                <input
                  type="text"
                  placeholder="Name"
                  className="block w-full mb-1"
                  value={enemies.name}
                  onChange={(e) =>
                    handleChangeNestedArray(
                      "backstory",
                      "relationships",
                      "enemies",
                      i,
                      n,
                      "name",
                      e.target.value, 
                      setCharacter
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Relationship"
                  className="block w-full mb-1"
                  value={enemies.relationship}
                  onChange={(e) =>
                    handleChangeNestedArray(
                      "backstory",
                      "relationships",
                      "enemies",
                      i,
                      n,
                      "relationship",
                      e.target.value, 
                      setCharacter
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Description"
                  className="block w-full"
                  value={enemies.description}
                  onChange={(e) =>
                    handleChangeNestedArray(
                      "backstory",
                      "relationships",
                      "enemies",
                      i,
                      n,
                      "description",
                      e.target.value, 
                      setCharacter
                    )
                  }
                />
              </div>
            ))}
            <button
              type="button"
              className="text-blue-600 underline"
              onClick={() =>
                addListItem(
                  "backstory",
                  "relationships",
                  "enemies",
                  { name: "", relationship: "", description: "" },
                  setCharacter,
                  i
                )
              }
            >
              Add Enemy
            </button>

            <h4 className="font-medium mt-4">Family</h4>
            {relationship.familyMembers.map((familyMembers, f) => (
              <div key={f} className="border p-2 mb-2">
                <input
                  type="text"
                  placeholder="Name"
                  className="block w-full mb-1"
                  value={familyMembers.name}
                  onChange={(e) =>
                    handleChangeNestedArray(
                      "backstory",
                      "relationships",
                      "familyMembers",
                      i,
                      f,
                      "name",
                      e.target.value, 
                      setCharacter
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Relationship"
                  className="block w-full mb-1"
                  value={familyMembers.relationship}
                  onChange={(e) =>
                    handleChangeNestedArray(
                      "backstory",
                      "relationships",
                      "familyMembers",
                      i,
                      f,
                      "relationship",
                      e.target.value, 
                      setCharacter
                    )
                  }
                />
                <input
                  type="text"
                  placeholder="Description"
                  className="block w-full"
                  value={familyMembers.description}
                  onChange={(e) =>
                    handleChangeNestedArray(
                      "backstory",
                      "relationships",
                      "familyMembers",
                      i,
                      f,
                      "description",
                      e.target.value, 
                      setCharacter
                    )
                  }
                />
              </div>
            ))}
            <button
              type="button"
              className="text-blue-600 underline"
              onClick={() =>
                addListItem(
                  "backstory",
                  "relationships",
                  "familyMembers",
                  { name: "", relationship: "", description: "" },
                  setCharacter,
                  i
                )
              }
            >
              Add Family
            </button>
          </div>
        ))}
      </section>

      {/* Goals Section */}
      <section>
        <h3 className="text-xl font-semibold">Goals</h3>

        <h4 className="font-medium mt-4">Wants</h4>
        {character.goals.wants.map((want, i) => (
          <div key={i} className="border p-2 mb-2">
            <input
              type="text"
              placeholder="Want Description"
              className="block w-full mb-1"
              value={want.description}
              onChange={(e) =>
                handleChangeArray("goals", "wants", i, "description", e.target.value, setCharacter)
              }
            />
            <input
              type="text"
              placeholder="Timeline"
              className="block w-full"
              value={want.timeline}
              onChange={(e) =>
                handleChangeArray("goals", "wants", i, "timeline", e.target.value, setCharacter)
              }
            />
          </div>
        ))}
        <button
          type="button"
          className="text-blue-600 underline"
          onClick={() =>
            addListItem("goals", "wants", undefined, { description: "", timeline: "" }, setCharacter)
          }
        >
          Add Want
        </button>

        <h4 className="font-medium mt-4">Obstacles</h4>
        {character.goals.obstacles.map((obstacle, i) => (
          <div key={i} className="border p-2 mb-2">
            <input
              type="text"
              placeholder="Description"
              className="block w-full mb-1"
              value={obstacle.description}
              onChange={(e) =>
                handleChangeArray("goals", "obstacles", i, "description", e.target.value, setCharacter)
              }
            />
          </div>
        ))}
        <button
          type="button"
          className="text-blue-600 underline"
          onClick={() =>
            addListItem("goals", "obstacles", undefined, { description: "" }, setCharacter)
          }
        >
          Add Obstacle
        </button>

        <h4 className="font-medium mt-4">Internal Struggles</h4>
        {character.goals.internalStruggles.map((struggle, i) => (
          <div key={i} className="border p-2 mb-2">
            <input
              type="text"
              placeholder="Description"
              className="block w-full mb-1"
              value={struggle.description}
              onChange={(e) =>
                handleChangeArray(
                  "goals",
                  "internalStruggles",
                  i,
                  "description",
                  e.target.value,
                  setCharacter
                )
              }
            />
          </div>
        ))}
        <button
          type="button"
          className="text-blue-600 underline"
          onClick={() =>
            addListItem("goals", "internalStruggles", undefined, { description: "" }, setCharacter)
          }
        >
          Add Internal Struggle
        </button>

        <h4 className="font-medium mt-4">External Challenges</h4>
        {character.goals.externalChallenges.map((challenge, i) => (
          <div key={i} className="border p-2 mb-2">
            <input
              type="text"
              placeholder="Description"
              className="block w-full mb-1"
              value={challenge.description}
              onChange={(e) =>
                handleChangeArray(
                  "goals",
                  "externalChallenges",
                  i,
                  "description",
                  e.target.value,
                  setCharacter
                )
              }
            />
          </div>
        ))}
        <button
          type="button"
          className="text-blue-600 underline"
          onClick={() =>
            addListItem("goals", "externalChallenges", undefined, { description: "" }, setCharacter)
          }
        >
          Add External Challenge
        </button>
      </section>

      <button
        type="submit"
        className="bg-blue-600 text-white px-4 py-2 rounded"
      >
        Save Character
      </button>
    </form>
  );
};

export default AddCharacterForm;
